import { createContext, useContext } from 'react';

/**
 * Local bridge that lets a deferred express-wallet form (Apple Pay today) tell
 * its host view "the PSP confirmed I'm available — you can stop showing the
 * skeleton and reveal me", and lets observers read whether that has happened.
 *
 * `PreferredPaymentView` and `EmbeddedPreferredPaymentView` provide this, and only while they gate the whole
 * screen behind a skeleton until the native wallet is known-good
 * (`deferExpressWalletReveal`). The wallet form mounts hidden under that
 * skeleton so its SDK can boot; when it fires its existing readiness signal it
 * calls `onWalletReady`, the view drops the skeleton, and the real FTD paints.
 *
 * `null` (the default) means "no host is gating" — forms then behave exactly as
 * before (no-op), so this stays inert for DEFAULT/SINGLE and for non-deferred
 * wallets.
 */
export interface ExpressRevealValue {
  onWalletReady: () => void;
  /**
   * Whether the host has stopped gating. False means the wallet still hasn't
   * answered, so nothing on this screen is settled yet — analytics that
   * describe what the shopper saw must wait for it (see
   * `useTrackCheckoutPresented`).
   */
  isRevealed: boolean;
}

export const ExpressRevealContext = createContext<ExpressRevealValue | null>(null);

export const useExpressReveal = (): ExpressRevealValue | null => useContext(ExpressRevealContext);
